import type { Product, ProductCheckout } from '@entities/product/api/product.contract'
import { checkoutForSkin, type CheckoutSkin } from './checkoutStyle'

export type PreviewMoney = { amount: string; currency: string }

export type BuyerPaymentMethod = 'pix' | 'card' | 'boleto'

export type PreviewPublicCheckout = {
  checkoutId: string
  payment: {
    methods: BuyerPaymentMethod[]
    maxInstallments: number
    gateway: 'STRIPE'
    publishableKey: string
  }
  refundDays: number
  acceptsCoupon: boolean
  product: { name: string; description: string; coverImage: string; productType: string }
  offer: {
    offerId: string
    name: string
    price: PreviewMoney
  } | null
  orderBumps: Array<{
    id: string
    targetOfferId: string
    planId: string | null
    recurring: boolean
    title: string
    description: string
    callToAction: string
    imageUrl: string
    price: PreviewMoney
  }>
  presentation: {
    template: 'classic' | 'minimal'
    accent: 'green' | 'blue' | 'violet' | 'orange'
    bannerUrl: string
    bannerVideoUrl?: string
    blocks: { timer: boolean; reviews: boolean; bump: boolean; security: boolean }
    fields: { name: boolean; email: boolean; phone: boolean; document: boolean }
    market: 'national' | 'global'
  }
  funnel: never[]
  experiment?: { id: 'checkout-skin'; variant: CheckoutSkin }
}

export function projectCheckoutForPreview(
  product: Product,
  checkout: ProductCheckout,
  skin: CheckoutSkin = 'new',
): PreviewPublicCheckout {
  const styled = checkoutForSkin(checkout, skin)
  const primary =
    product.offers.find((offer) => offer.id === product.links.primaryOfferId) ??
    product.offers.find((offer) => offer.status === 'ACTIVE') ??
    product.offers[0]
  const offer = primary
    ? {
        offerId: primary.id,
        name: primary.name,
        price: { amount: primary.price.amount, currency: primary.price.currency },
      }
    : {
        offerId: 'principal',
        name: product.name,
        price: { amount: product.price.amount, currency: product.price.currency },
      }

  const buyerMethods: BuyerPaymentMethod[] = []
  if (product.paymentMethods.includes('CREDIT_CARD')) buyerMethods.push('card')

  return {
    checkoutId: checkout.id,
    payment: {
      methods: buyerMethods.length > 0 ? buyerMethods : ['card'],
      maxInstallments: 1,
      gateway: 'STRIPE',
      publishableKey: '',
    },
    refundDays: product.refundDays,
    acceptsCoupon: product.features.coupons && product.coupons.some((coupon) => coupon.active),
    product: {
      name: product.name,
      description: product.description,
      coverImage: product.coverImage,
      productType: product.productType,
    },
    offer,
    orderBumps: product.orderBumps.map((bump) => ({
      id: bump.id,
      targetOfferId: bump.targetOfferId,
      planId: bump.planId,
      recurring: bump.planId !== null,
      title: bump.title,
      description: bump.description,
      callToAction: bump.callToAction,
      imageUrl: bump.imageUrl,
      price: { amount: bump.price.amount, currency: bump.price.currency },
    })),
    presentation: {
      template: styled.template,
      accent: styled.accent,
      bannerUrl: styled.bannerUrl,
      bannerVideoUrl: styled.bannerVideoUrl,
      blocks: styled.blocks,
      fields: styled.fields,
      market: styled.market,
    },
    funnel: [],
    ...(skin === 'legacy' ? { experiment: { id: 'checkout-skin' as const, variant: skin } } : {}),
  }
}
